import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Lock } from "lucide-react";
import api from '../../utils/api';

const AuthComponent = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'buyer', // Default to general user
    company: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.auth.register(formData);
      
      if (response.token) {
        // User is active (non-verifier), proceed with login
        api.utils.setToken(response.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        navigate('/dashboard');
      } else {
        // Verifier pending approval
        alert(response.message || 'Verifier registration submitted for review. You will be notified once approved.');
        setIsLogin(true); // Switch to login form
        setFormData({ ...formData, password: '' }); // Clear password
      }
    } catch (error) {
      alert('Registration failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.auth.login({
        email: formData.email,
        password: formData.password
      });
      api.utils.setToken(response.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/dashboard');
    } catch (error) {
      alert('Login failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-green-500 to-blue-500 text-white relative">
      {/* Logo in Top Left */}
      <div className="absolute top-6 left-6 flex items-center cursor-pointer">
        <h1 className="text-2xl font-bold text-white">Climify</h1>
        <span className="ml-2 bg-white bg-opacity-20 text-[12px] mb-3  px-2 rounded-full text-white">
          BETA
        </span>
      </div>

      {/* Glassmorphic Auth Form */}
      <div className="bg-white bg-opacity-10 backdrop-blur-md p-8 rounded-lg shadow-lg w-full max-w-md text-center">
        <h2 className="text-white text-3xl font-semibold mb-6">
          {isLogin ? 'Login' : 'Register'}
        </h2>

        <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
          {/* Name Input (Registration only) */}
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-300" size={20} />
              <input
                type="text"
                placeholder="Full Name"
                className="w-full py-3 pl-10 pr-4 bg-white bg-opacity-20 text-white placeholder-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
          )}

          {/* Email Input */}
          <div className="relative">
            <User className="absolute left-3 top-3 text-gray-300" size={20} />
            <input
              type="email"
              placeholder="Email Address"
              className="w-full py-3 pl-10 pr-4 bg-white bg-opacity-20 text-white placeholder-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {/* Company Input (Registration only) */}
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-300" size={20} />
              <input
                type="text"
                placeholder="Company Name"
                className="w-full py-3 pl-10 pr-4 bg-white bg-opacity-20 text-white placeholder-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              />
            </div>
          )}

          {/* Account Type Selection (Registration only) */}
          {!isLogin && (
            <div className="relative">
              <select
                className="w-full py-3 px-4 bg-white bg-opacity-20 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <option value="buyer" className="text-black">General User (Can buy credits and create projects)</option>
                <option value="verifier" className="text-black">Verifier (Requires admin approval)</option>
              </select>
            </div>
          )}

          {/* Password Input */}
          <div className="relative">
            <Lock className="absolute left-3 top-3 text-gray-300" size={20} />
            <input
              type="password"
              placeholder="Password"
              className="w-full py-3 pl-10 pr-4 bg-white bg-opacity-20 text-white placeholder-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between text-sm text-gray-200">
            <label className="flex items-center space-x-2">
              <input type="checkbox" className="accent-white" />
              <span>Remember Me</span>
            </label>
            <button
              type="button"
              className="hover:underline"
              // onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white bg-opacity-30 text-white py-3 rounded-md hover:bg-opacity-40 transition duration-300 disabled:opacity-50"
          >
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
          </button>
        </form>

        {/* Toggle between Login and Register */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-white hover:underline"
          >
            {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthComponent;
